import React from "react";
import { useTheme } from "@mui/material";
import FlatfileImporter, {
  FlatfileResults,
  ISettings,
} from "@flatfile/adapter";
import { getFlatFileTheme } from "./FlatFileProvider.helpers";

export type FlatFileTheme = ISettings["theme"];
export type FlatFileFields = ISettings["fields"];

interface FlatFileContextValue {
  licenseKey: string;
  userId?: string;
  theme: FlatFileTheme;
}

interface FlatFileProviderProps {
  children?: React.ReactNode;
  licenseKey: string;
  /** Logged-in user id (injected by the app shell). */
  userId?: string;
}

const FlatFileContext = React.createContext<FlatFileContextValue | null>(null);

function FlatFileProvider(props: FlatFileProviderProps) {
  const { children, licenseKey, userId } = props;

  const muiTheme = useTheme();

  const theme = React.useMemo(() => getFlatFileTheme(muiTheme), [muiTheme]);

  const contextValue = React.useMemo(
    () => ({
      licenseKey,
      userId,
      theme,
    }),
    [licenseKey, userId, theme]
  );

  return (
    <FlatFileContext.Provider value={contextValue}>
      {children}
    </FlatFileContext.Provider>
  );
}

interface UseFlatFileImporterProps {
  type: string;
  fields: FlatFileFields;
}

type UseFlatFileImporterResult = () => Promise<{
  data: FlatfileResults | null;
  error: any;
}>;

export function useFlatFileImporter(
  props: UseFlatFileImporterProps
): UseFlatFileImporterResult {
  const { type, fields } = props;

  const contextValue = React.useContext(FlatFileContext);

  if (!contextValue) {
    throw Error("Should be used inside the FlatFileProvider");
  }

  const { licenseKey, userId, theme } = contextValue;

  const importer = React.useCallback(async () => {
    const flatFileImporter = new FlatfileImporter(licenseKey, {
      type,
      fields,
      theme,
      managed: true,
      maxRecords: 10000,
    });

    flatFileImporter.setCustomer({
      userId: userId!,
    });

    try {
      const result = await flatFileImporter.requestDataFromUser();
      flatFileImporter.displayLoader("Please wait while your data is loading");
      flatFileImporter.displaySuccess("Success");
      return { data: result, error: null };
    } catch (e) {
      if (!e) {
        return { data: null, error: null };
      }
      flatFileImporter.displaySuccess("Something went wrong!");
      return { data: null, error: e };
    }
  }, [fields, licenseKey, theme, type, userId]);

  return importer;
}

export default FlatFileProvider;
